import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState, AppThunk} from '../../app/store';

export type ScreenMode =
    "mode1_Agreement" |
    "mode2_Top" |
    "mode3_TopModal" |
    "mode4_Direction" |
    "mode5_Pray" |
    "mode6_Ready" |
    "mode7_Clap" |
    "mode8_Loading" |
    "mode9_Result" |
    "mode10_Error"

export type Directions =
    undefined |
    "direction1_Kita" |
    "direction2_Tohoku" |
    "direction3_Higashi" |
    "direction4_Nanto" |
    "direction5_Minami" |
    "direction6_Nansei" |
    "direction7_Nishi" |
    "direction8_Hokusei"

export interface OmikujiState {
    isLoading: boolean
    successMessage: string
    errorMessage: string
    screenMode: ScreenMode

    clapSound?: string
    kichi?: string
    kichiId?: string
    recordedAt?: Date
    direction?: Directions
    isShowModal: boolean
    isTermAgree: boolean
}

const initialState: OmikujiState = {
    isLoading: false,
    successMessage: "",
    errorMessage: "",
    screenMode: "mode2_Top",
    clapSound: undefined,
    kichi: undefined,
    kichiId: undefined,
    recordedAt: undefined,
    direction: undefined,
    isShowModal: false,
    isTermAgree: false,
};

export const inferOmikuji = createAsyncThunk(
    'omikuji/infer',
    async (amount: number) => {
        const response = await new Promise((r) => setTimeout(() => {
            r("done!")
        }, 500));
        // The value we return becomes the `fulfilled` action payload
        return response
    }
);

export const omikujiSlice = createSlice({
    name: 'omikuji',
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        setTermAgree: (state, action: PayloadAction<boolean>) => {
            state.isTermAgree = action.payload
        },
        setShowModal: (state, action: PayloadAction<boolean>) => {
            state.isShowModal = action.payload
        },
        setMode: (state, action: PayloadAction<ScreenMode>) => {
            // Redux Toolkit allows us to write "mutating" logic in reducers. It
            // doesn't actually mutate the state because it uses the Immer library,
            // which detects changes to a "draft state" and produces a brand new
            // immutable state based off those changes
            state.screenMode = action.payload
        },
        setDirection: (state, action: PayloadAction<Directions>) => {
            // Redux Toolkit allows us to write "mutating" logic in reducers. It
            // doesn't actually mutate the state because it uses the Immer library,
            // which detects changes to a "draft state" and produces a brand new
            // immutable state based off those changes
            state.direction = action.payload
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload
        },
        setKichi: (state, action: PayloadAction<string|undefined>) => {
            state.kichi = action.payload
        },
        setKichiId: (state, action: PayloadAction<string>) => {
            state.kichiId = action.payload
        },
        setClapFile: (state, action: PayloadAction<string>) => {
            state.clapSound = action.payload
        },
        clear: (state) => {
            state = initialState
        },
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(inferOmikuji.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(inferOmikuji.fulfilled, (state, action) => {
                state.isLoading = false
                state.successMessage = "success message"
            })
            .addCase(inferOmikuji.rejected, (state) => {
                state.isLoading = false
                state.errorMessage = "error message"
            });
    },
});

export const {
    setKichi,
    setKichiId,
    setMode,
    setDirection,
    setShowModal,
    setTermAgree,
    setClapFile,
    setLoading,
    clear
} = omikujiSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectOmikujiState = (state: RootState) => state.omikuji;

export const selectScreenMode = (state: RootState) => state.omikuji.screenMode;
export const selectDirection = (state: RootState) => state.omikuji.direction;
export const selectShowModal = (state: RootState) => state.omikuji.isShowModal;

export const selectIsTermAgree = (state: RootState) => state.omikuji.isTermAgree;

export const selectClapSound = (state: RootState) => state.omikuji.clapSound;
export const selectKichi = (state: RootState) => state.omikuji.kichi;
export const selectKichiId = (state: RootState) => state.omikuji.kichiId;

// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
// export const incrementIfOdd =
//   (amount: number): AppThunk =>
//   (dispatch, getState) => {
//     const currentValue = selectCount(getState());
//     if (currentValue % 2 === 1) {
//       dispatch(incrementByAmount(amount));
//     }
//   };

export default omikujiSlice.reducer;
